/**
 * Place Link Processor
 *
 * Single Responsibility: Converts [Place]() markdown links to React components
 * Open/Closed Principle: Can be extended with new place rendering strategies
 */

import React from 'react';
import { PlaceName } from '../PlaceName';
import { LocationTextProcessor } from './LocationTextProcessor';
import { ITextProcessor, TextProcessorOptions } from './ITextProcessor';

export class PlaceLinkProcessor implements ITextProcessor {
  process(
    text: string,
    keyPrefix: string,
    options?: TextProcessorOptions
  ): React.ReactNode[] {
    // Get valid locations (with coordinates) and deduplicated
    const validLocations =
      options?.locations && options.locations.length > 0
        ? LocationTextProcessor.getValidLocations(options.locations)
        : [];

    // Create a set of valid location names for quick lookup (case-insensitive)
    // Only locations with valid coordinates should be rendered as tags
    const validPlaceNames = new Set<string>();
    validLocations.forEach(loc => {
      const location = loc as {
        place?: string;
        title?: string;
        place_name?: string;
        latitude?: number;
        longitude?: number;
      };
      if (location.place) {
        validPlaceNames.add(location.place.toLowerCase().trim());
      }
      if (location.title) {
        validPlaceNames.add(location.title.toLowerCase().trim());
      }
      if (location.place_name) {
        validPlaceNames.add(location.place_name.toLowerCase().trim());
      }
    });

    const parts: React.ReactNode[] = [];
    const linkRegex = /\[([^\]]+)\]\(\)/g;
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        const beforeText = text.substring(lastIndex, match.index);
        if (beforeText) {
          parts.push(
            <span key={`${keyPrefix}-text-${lastIndex}`}>{beforeText}</span>
          );
        }
      }

      // Get the place name
      const placeName = match[1].trim();
      const normalizedPlaceName = placeName.toLowerCase().trim();

      // Only render as PlaceName component if the location has valid coordinates
      // If not in validPlaceNames, just render as plain text (remove brackets)
      if (validPlaceNames.has(normalizedPlaceName)) {
        parts.push(
          <PlaceName
            key={`${keyPrefix}-place-${match.index}`}
            place={placeName}
            onClick={() => options?.onPlaceClick?.(placeName)}
          />
        );
      } else {
        // Location doesn't have valid coordinates, but still make it clickable
        // This ensures all place links can open the place details page
        parts.push(
          <PlaceName
            key={`${keyPrefix}-place-${match.index}`}
            place={placeName}
            onClick={() => options?.onPlaceClick?.(placeName)}
          />
        );
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      if (remainingText) {
        parts.push(
          <span key={`${keyPrefix}-text-${lastIndex}`}>{remainingText}</span>
        );
      }
    }

    // If no parts were created (no matches found), return the original text
    if (parts.length === 0) {
      return [<span key={keyPrefix}>{text}</span>];
    }

    // Return the processed parts
    return parts;
  }
}
